/* eslint-disable max-lines */
import { segment } from '@actions/analytics';
import { useState, useEffect } from 'react';
import { useSnackbar } from '@stores/snackbar';
import { getIsLoggedIn } from '@api/selectors/auth';
import { Spacings } from '@styles/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { readCookie } from '@utils/cookies';
import EventsHome from '@cont/Event/EventsHome';
import {
  checkIfYoutubeMappingExists,
  recordYoutubeMapping
} from '@api/unacademyEvents';
import SimpleFlexLayout from '@layout/SimpleFlexLayout/SimpleFlexLayout';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import Image from '@base/FutureImage';
import Button from '@base/Button/Button';
import P1 from '@base/P1/P1';
import TextInput from '@cont/Internal/GenericBanner/Form/TextInputWithErrorState';
import deviceHelper from '@utils/deviceHelper';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { BACKEND_API_ERROR } from '@constants/index';

const eventMap = {
  CLICKED_ON_REGISTER: 'GTM - Clicked on Register',
  EVENT_REGISTERED: 'GTM - Event Registered',
  FIND_YOUTUBE_ID_CLICKED: 'GTM - Find YouTube ID Clicked'
};

const youtubeIDCheckURL = 'https://www.youtube.com/account_advanced';

const EventRegistration = () => {
  const { myInfo } = useMyInfo();
  const isLoggedIn = getIsLoggedIn(myInfo);
  const [isMobile, setIsMobile] = useState(false);
  const [youtubeID, setYoutubeID] = useState('');
  const [error, setError] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { open: showSnackbar } = useSnackbar();

  const recordYoutubeID = async () => {
    const response = await recordYoutubeMapping({
      accessToken: readCookie('accessToken'),
      youtube_id: youtubeID
    });

    if (response?.error) {
      showSnackbar({
        message: BACKEND_API_ERROR,
        time: 5000,
        type: 'error'
      });
    } else {
      setIsRegistered(true);

      segment.track(eventMap.EVENT_REGISTERED, {
        gtm_event_name: 'yt_polls',
        youtube_user_id: youtubeID
      });
    }
  };

  useEffect(() => {
    setIsMobile(deviceHelper.isMobile({ isServer: false }));

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    let cancelled = false;
    const checkMapping = async () => {
      const response = await checkIfYoutubeMappingExists({
        accessToken: readCookie('accessToken')
      });
      if (cancelled) return;
      if (response?.error) {
        showSnackbar({
          message: BACKEND_API_ERROR,
          time: 5000,
          type: 'error'
        });
      } else {
        if (response?.hasYoutubeId && response?.youtubeId) {
          setYoutubeID(response.youtubeId);
        }
        setIsRegistered(response?.hasYoutubeId ?? false);
      }
    };
    checkMapping();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  let wrapperPaddingTop = '10vh';
  let wrapperPaddingBottom = '10vh';
  let contentPaddingTop = '';
  if (isMobile && isRegistered) {
    wrapperPaddingTop = '8px';
    wrapperPaddingBottom = '8px';
    contentPaddingTop = '8px';
  } else if (isMobile) {
    wrapperPaddingTop = '20px';
    wrapperPaddingBottom = '';
    contentPaddingTop = '20px';
  }

  const eventBannerImage = isLoggedIn
    ? '/static/images/new_event_banner.png'
    : '/static/images/event_banner.svg';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyItems: 'center',
        flexGrow: 1,
        margin: 'auto',
        paddingLeft: isMobile ? '' : '20vh',
        paddingRight: isMobile ? '' : '20vh',
        paddingTop: wrapperPaddingTop,
        paddingBottom: wrapperPaddingBottom,
        backgroundColor: 'var(--color-base-1)',
        zIndex: 3,
        marginTop: 1,
        marginBottom: -38,
        width: '100%',
        justifyContent: 'center'
      }}
    >
      {!isRegistered && (
        <div
          style={{
            padding: isMobile ? '' : '20px',
            height: isMobile ? '234.39px' : '36.5vh',
            width: isMobile ? '328px' : '75.6vh',
            backgroundImage: `url("${eventBannerImage}")`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: isMobile ? '8.93px' : '12.5px',
            display: 'flex'
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: contentPaddingTop,
          // eslint-disable-next-line no-nested-ternary
          paddingBottom: isMobile || isLoggedIn ? '' : '20px',
          paddingLeft: isRegistered ? '28px' : '20px',
          paddingRight: '20px',
          // eslint-disable-next-line no-nested-ternary
          height: isMobile ? '68.5vh' : isRegistered ? '' : '36.5vh', // Match the height of the image
          justifyContent: isRegistered ? '' : 'space-between',
          // eslint-disable-next-line no-nested-ternary
          alignItems: isRegistered ? (isMobile ? 'center' : '') : ''
        }}
      >
        {isRegistered ? (
          <EventsHome youtubeId={youtubeID} email={myInfo?.email} />
        ) : (
          <div>
            {isMobile ? (
              <h2
                style={{
                  color: 'var(--color-i-black)',
                  lineHeight: '24px',
                  fontWeight: '600'
                }}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoggedIn
                  ? 'You’re just one step away from winning exciting prizes!'
                  : 'Register or login to continue'}
              </h2>
            ) : (
              <h1
                style={{
                  color: 'var(--color-i-black)',
                  lineHeight: '40px',
                  fontWeight: '700',
                  marginTop: isLoggedIn ? 0 : ''
                }}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoggedIn
                  ? 'You’re just one step away from winning exciting prizes!'
                  : 'Register or login to continue'}
              </h1>
            )}
            <div
              style={{
                marginTop: isMobile ? '20px' : ''
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Image
                  src="sign-checkmark.svg"
                  width={isMobile ? 20 : 24}
                  height={isMobile ? 20 : 24}
                  alt="Event Registration"
                />
                <P1
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: isMobile ? '18px' : '24px',
                    color: 'var(--color-i-black)',
                    marginBottom: Spacings.SPACING_2B,
                    marginLeft: Spacings.SPACING_1B
                  }}
                >
                  {isLoggedIn
                    ? 'Enter your YouTube channel ID below'
                    : 'Register to participate in polls on Youtube'}
                </P1>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Image
                  src="sign-checkmark.svg"
                  width={isMobile ? 20 : 24}
                  height={isMobile ? 20 : 24}
                  alt="Event Registration"
                />
                <P1
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: isMobile ? '18px' : '24px',
                    color: 'var(--color-i-black)',
                    marginBottom: Spacings.SPACING_2B,
                    marginLeft: Spacings.SPACING_1B
                  }}
                >
                  Only registered users would be counted in poll leaderboard
                </P1>
              </div>
            </div>
          </div>
        )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {isRegistered ? (
          <></>
        ) : isLoggedIn ? (
          <>
            {isMobile ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    marginTop: 20
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%'
                    }}
                  >
                    <TextInput
                      style={{
                        height: '48px'
                      }}
                      placeholder="Enter your YouTube channel ID"
                      value={youtubeID}
                      onChange={(val) => {
                        setYoutubeID(val);

                        if (val.includes('@')) {
                          setError(true);
                        } else {
                          setError(false);
                        }
                      }}
                      hasError={error}
                    />
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                    <p
                      style={{
                        alignSelf: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        color: 'var(--color-i-black)',
                        cursor: 'pointer',
                        flexBasis: '20%',
                        marginLeft: 10
                      }}
                      onClick={() => {
                        segment.track(eventMap.FIND_YOUTUBE_ID_CLICKED, {
                          gtm_event_name: 'yt_polls'
                        });

                        window.open(youtubeIDCheckURL, '_blank');
                      }}
                    >
                      Get ID &#8599;
                    </p>
                  </div>
                  {error && (
                    <p
                      style={{
                        fontWeight: '400',
                        fontSize: 12,
                        lineHeight: '16px',
                        letterSpacing: '0%',
                        color: 'var(--color-i-red)',
                        alignSelf: 'flex-start',
                        marginTop: 4
                      }}
                    >
                      Error : Channel ID cannot contain ‘@’.
                    </p>
                  )}
                  <P1
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: 'var(--color-text-secondary)',
                      marginTop: Spacings.SPACING_2B
                    }}
                  >
                    Click &apos;Get ID&apos; to find your YouTube ID, then paste
                    it in the field above.
                  </P1>
                </div>
                <Button
                  label="Submit"
                  onClick={() => {
                    recordYoutubeID();
                  }}
                  size="small"
                  variant={
                    (youtubeID?.length ?? 0) >= 15 && !error
                      ? 'filled'
                      : 'disabled'
                  }
                  color="black"
                  grid={isMobile ? 5 : 4}
                  style={{
                    marginTop: 'auto',
                    position: 'sticky',
                    bottom: '3%',
                    zIndex: 100
                  }}
                />
              </>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginTop: 20
                  }}
                >
                  <TextInput
                    style={{
                      height: '48px'
                    }}
                    placeholder="Enter your YouTube Channel ID"
                    value={youtubeID}
                    onChange={(val) => {
                      setYoutubeID(val);

                      if (val.includes('@')) {
                        setError(true);
                      } else {
                        setError(false);
                      }
                    }}
                    hasError={error}
                  />
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                  <p
                    style={{
                      alignSelf: 'center',
                      fontSize: '14px',
                      fontWeight: '700',
                      color: 'var(--color-i-black)',
                      cursor: 'pointer',
                      flexBasis: '20%',
                      marginLeft: 10
                    }}
                    onClick={() => {
                      segment.track(eventMap.FIND_YOUTUBE_ID_CLICKED, {
                        gtm_event_name: 'yt_polls'
                      });

                      window.open(youtubeIDCheckURL, '_blank');
                    }}
                  >
                    Get ID &#8599;
                  </p>
                </div>
                {error && (
                  <p
                    style={{
                      fontWeight: '400',
                      fontSize: 14,
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      color: 'var(--color-i-red)',
                      marginTop: 6
                    }}
                  >
                    Error : Channel ID cannot contain ‘@’.
                  </p>
                )}
                <P1
                  style={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: 'var(--color-text-secondary)',
                    marginTop: Spacings.SPACING_2B
                  }}
                >
                  Click &apos;Get ID&apos; to find your YouTube channel ID, then
                  paste it in the field above.
                </P1>
                <Button
                  label="Submit"
                  onClick={() => {
                    recordYoutubeID();
                  }}
                  size="small"
                  variant={
                    (youtubeID?.length ?? 0) >= 15 && !error
                      ? 'filled'
                      : 'disabled'
                  }
                  color="black"
                  grid={isMobile ? 5 : 4}
                  style={{ marginTop: 'auto' }}
                />
              </>
            )}
          </>
        ) : (
          <Button
            label="Register or login"
            onClick={() => {
              openLoginDrawer();

              segment.track(eventMap.CLICKED_ON_REGISTER, {
                gtm_event_name: 'yt_polls',
                youtube_id: youtubeID
              });
            }}
            size="small"
            variant="filled"
            color="black"
            grid={isMobile ? 5 : 4}
            style={{
              marginTop: 'auto',
              position: isMobile ? 'sticky' : '',
              bottom: isMobile ? '3%' : '',
              zIndex: isMobile ? 100 : ''
            }}
          />
        )}
      </div>
    </div>
  );
};

EventRegistration.pageInfo = {
  isMWebSupported: true,
  name: 'Event Registration Page',
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true,
    disableDarkMode: true,
    showLogin: true // Add this to enable login handling in layout
  }
};

EventRegistration.Layout = SimpleFlexLayout;

export default EventRegistration;
EventRegistration.Wrapper = PageReduxProvider;
